<script setup lang="ts">
/**
 * Message input of a chat: an auto-growing textarea with a send button (a
 * cancel button while an answer is generated and `cancellable`), an optional
 * `#toolbar` above, `#leading` left of the input (e.g. a microphone button)
 * and a `#footer` below. Enter sends, Shift+Enter breaks the line.
 *
 * Slash commands (`commands`): a message starting with „/“ opens a list of the
 * matching commands — by prefix, case and accents do not matter („/ruck“ finds
 * „rückruf“). Arrow keys walk it, Enter or Tab completes, Escape closes; Enter
 * on a fully typed command sends at once. Sending a message whose first word
 * is a known command emits `command` ({ name, args, text, mentions }) INSTEAD
 * of `send`; an unknown „/word“ is sent as an ordinary message.
 *
 * @-mentions (`mentionCandidates`): „@“ opens the people picker of
 * `useMentionComposer`. `send` then carries the picked mentions as a second
 * argument and `command` the ones inside its arguments, so „/aktivität @Timo
 * …“ hands over Timo's id instead of a name to parse back out of the text.
 *
 * `canSend` lets the message go out without text, while the caller holds
 * something that is sent with it (a file, a record card): the send button and
 * Enter work on an empty input and `send` carries an empty string.
 *
 * Writing at the caret: the exposed `insertText(text)` puts text over the
 * selection (an emoji, a prefilled phrase), `wrapSelection(before, after)`
 * wraps it. Both keep the command list and the picked mentions in step with
 * the text: a mention inside a wrapped selection stays a mention, one the
 * write cuts or replaces goes. With `formatShortcuts`, Ctrl/Cmd+B, +I and +U wrap the selection
 * in `**`, `_` and `++` (bold, italic, underline of a Markdown message).
 *
 * Without the new props and `commands` / `mentionCandidates` it behaves as it
 * always did.
 */
import { computed, nextTick, ref, useId, watch } from 'vue';
import Send from 'lucide-vue-next/dist/esm/icons/send.js'; import Square from 'lucide-vue-next/dist/esm/icons/square.js';
import NxAvatar from '../Avatar/NxAvatar.vue';
import type { NxMention, NxMentionCandidate } from '../Mentions/types';
import { useMentionComposer } from '../Mentions/useMentionComposer';
import {
  chatCommandQueryAt,
  isExactChatCommand,
  matchChatCommands,
  parseChatCommand,
  type NxChatCommand,
  type NxChatCommandInvocation,
} from './chatCommands';

const props = withDefaults(
  defineProps<{
    modelValue?: string;
    placeholder?: string;
    disabled?: boolean;
    sending?: boolean;
    /** Show cancel (stop generation) instead of send while sending. */
    cancellable?: boolean;
    maxHeight?: number;
    /** Slash commands offered after a leading „/“. */
    commands?: readonly NxChatCommand[];
    /** People for the @-picker. Unset: no @-mentions. */
    mentionCandidates?: readonly NxMentionCandidate[];
    /**
     * Sending needs no text: the caller has something attached that goes out
     * with the message (a file, a record card). Default: only text sends.
     */
    canSend?: boolean;
    /**
     * Ctrl/Cmd+B, +I and +U wrap the selection in `**`, `_` and `++`. Off by
     * default: the keys do nothing in a plain input.
     */
    formatShortcuts?: boolean;
    /**
     * `pill`: no bar around it — a round field with the leading controls and a
     * round send button beside it, `#field-end` inside the field's end.
     */
    shape?: 'default' | 'pill';
    /** Accessible name of the input (default: none, the placeholder names it). */
    inputLabel?: string;
    sendLabel?: string;
    cancelLabel?: string;
    /** Accessible name of the command list. */
    commandsLabel?: string;
    /** Accessible name of the people list. */
    mentionsLabel?: string;
  }>(),
  {
    modelValue: '',
    placeholder: '',
    disabled: false,
    sending: false,
    cancellable: false,
    maxHeight: 160,
    canSend: false,
    formatShortcuts: false,
    shape: 'default',
    commands: () => [],
    mentionCandidates: undefined,
    inputLabel: undefined,
    sendLabel: 'Senden',
    cancelLabel: 'Abbrechen',
    commandsLabel: 'Befehle',
    mentionsLabel: 'Personen',
  }
);

const emit = defineEmits<{
  'update:modelValue': [v: string];
  /**
   * The trimmed message. With `mentionCandidates` also its mentions, indexed
   * into the message.
   */
  send: [content: string, mentions?: NxMention[]];
  /** A message whose first word is a known slash command — sent instead of `send`. */
  command: [invocation: NxChatCommandInvocation];
  cancel: [];
  /** Files pasted into the field. Text pastes as always. */
  files: [files: File[]];
}>();

defineSlots<{
  toolbar?(): unknown;
  /** Left of the input, e.g. a microphone button. */
  leading?(): unknown;
  /** Inside the end of the field (`shape="pill"`), e.g. an emoji button. */
  'field-end'?(): unknown;
  footer?(): unknown;
  'send-icon'?(): unknown;
  'cancel-icon'?(): unknown;
}>();

const listId = useId();
const textarea = ref<HTMLTextAreaElement | null>(null);

const content = ref(props.modelValue);

// @-mentions: the headless engine of the Mentions module, fed the textarea's
// text and caret. Off (and untouched) while `mentionCandidates` is unset.
const mentionsOn = computed(() => props.mentionCandidates !== undefined);
const mention = useMentionComposer({ candidates: () => [...(props.mentionCandidates ?? [])] });
const { candidates: people, pickerIndex: personIndex } = mention;
mention.reset(content.value);

// Slash commands: the text after a leading „/“ while the caret is in the first word.
const commandQuery = ref<string | null>(null);
const commandIndex = ref(0);
const commandMatches = computed(() =>
  commandQuery.value === null ? [] : matchChatCommands(props.commands, commandQuery.value)
);

const commandOpen = computed(() => commandMatches.value.length > 0);
const mentionOpen = computed(
  () =>
    mentionsOn.value && !commandOpen.value && mention.showPicker.value && people.value.length > 0
);
const pickerOpen = computed(() => commandOpen.value || mentionOpen.value);
const pickersAvailable = computed(() => props.commands.length > 0 || mentionsOn.value);
const activeIndex = computed(() =>
  commandOpen.value
    ? Math.min(commandIndex.value, commandMatches.value.length - 1)
    : personIndex.value
);

watch(
  () => props.modelValue,
  (v) => {
    if (v === content.value) return;
    content.value = v;
    // Text set from outside carries no picked mentions and closes the pickers.
    mention.reset(v);
    commandQuery.value = null;
  }
);
watch(content, (v) => {
  emit('update:modelValue', v);
  nextTick(resize);
});

function resize() {
  const el = textarea.value;
  if (!el) return;
  el.style.height = 'auto';
  // The input is border-box: `scrollHeight` is the content plus padding, so the
  // borders come on top — without them an empty line overflows by their width
  // and a scrollbar shows.
  const style = getComputedStyle(el);
  const borders =
    (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
  el.style.height = Math.min(el.scrollHeight + borders, props.maxHeight) + 'px';
}

/** Opens, narrows or closes the command list for the query at the caret. */
function setCommandQuery(query: string | null) {
  if (query !== commandQuery.value) commandIndex.value = 0;
  commandQuery.value = query;
}

/** Reads text and caret again: which picker is open, and for what. */
function detect() {
  const el = textarea.value;
  if (!el) return;
  const caret = el.selectionStart ?? el.value.length;
  setCommandQuery(props.commands.length ? chatCommandQueryAt(el.value, caret) : null);
  if (mentionsOn.value) mention.handleInput(el.value, caret);
}

function onInput() {
  resize();
  detect();
}

function onKeyup(e: KeyboardEvent) {
  // The caret moved sideways: it may have left (or entered) a command or mention.
  if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') detect();
}

function onBlur() {
  commandQuery.value = null;
  mention.closePicker();
}

/**
 * Puts `value` into the input with the caret at `caret` — or, with `selectionEnd`,
 * the text from `caret` to there selected. A write from outside the keyboard
 * never reaches the input event, so the pickers are read again here: the
 * command list follows the new text and caret, and `mentions` (the picked
 * mentions, moved with the edit by `moveMentions`) replace the engine's own
 * guess at what the edit did to them.
 */
function applyText(
  value: string,
  caret: number,
  selectionEnd = caret,
  mentions?: readonly NxMention[]
) {
  content.value = value;
  setCommandQuery(props.commands.length ? chatCommandQueryAt(value, caret) : null);
  if (mentionsOn.value) {
    mention.handleInput(value, caret);
    if (mentions) mention.mentions.value = mentions.map((m) => ({ ...m }));
  }
  nextTick(() => {
    const el = textarea.value;
    if (!el) return;
    if (el.value !== value) el.value = value;
    el.focus();
    el.setSelectionRange(caret, selectionEnd);
    resize();
  });
}

/**
 * The picked mentions after `[start, end)` of the text was replaced by
 * `inserted` characters: those before it stay, those behind it move along,
 * those the edit touches are gone — the same rule as an edit by keyboard, but
 * exact where a text diff could not tell an insertion from its neighbours.
 */
function moveMentions(
  mentions: readonly NxMention[],
  start: number,
  end: number,
  inserted: number
): NxMention[] {
  const delta = inserted - (end - start);
  const moved: NxMention[] = [];
  for (const m of mentions) {
    if (m.end <= start) moved.push(m);
    else if (m.start >= end) moved.push({ ...m, start: m.start + delta, end: m.end + delta });
  }
  return moved;
}

/** The selection of the input, or the end of the text while it has none to report. */
function selectionRange(): [number, number] {
  const el = textarea.value;
  const length = content.value.length;
  const start = Math.min(el?.selectionStart ?? length, length);
  const end = Math.min(el?.selectionEnd ?? length, length);
  return start <= end ? [start, end] : [end, start];
}

/** Writes `text` over the selection (at the caret when nothing is selected); the caret ends up behind it. */
function insertText(text: string) {
  if (props.disabled || !text) return;
  const [start, end] = selectionRange();
  const value = content.value;
  applyText(
    value.slice(0, start) + text + value.slice(end),
    start + text.length,
    start + text.length,
    moveMentions(mention.mentions.value, start, end, text.length)
  );
}

/**
 * Puts `before` and `after` round the selection and keeps the wrapped text
 * selected; without a selection it writes both and leaves the caret between.
 * Mentions inside the selection stay mentions: the two marks are two
 * insertions, none of which replaces a mention.
 */
function wrapSelection(before: string, after: string) {
  if (props.disabled || (!before && !after)) return;
  const [start, end] = selectionRange();
  const value = content.value;
  const inner = start + before.length;
  const opened = moveMentions(mention.mentions.value, start, start, before.length);
  const closed = end + before.length;
  const wrapped = moveMentions(opened, closed, closed, after.length);
  applyText(
    value.slice(0, start) + before + value.slice(start, end) + after + value.slice(end),
    inner,
    inner + (end - start),
    wrapped
  );
}

/** Marks the format shortcuts write: key → Markdown pair. */
const FORMAT_MARKS: Readonly<Record<string, string>> = { b: '**', i: '_', u: '++' };

/** Replaces the typed command word with the full one, followed by a space. */
function complete(command: NxChatCommand) {
  const text = content.value;
  const end = /^\/\S*/.exec(text)?.[0].length ?? 0;
  const rest = text.slice(end);
  const word = `/${command.name}`;
  commandQuery.value = null;
  applyText(rest ? word + rest : `${word} `, word.length + 1);
}

function pick(index: number) {
  if (commandOpen.value) {
    const command = commandMatches.value[index];
    if (command) complete(command);
    return;
  }
  const person = people.value[index];
  if (!person) return;
  const caret = mention.applyCandidate(person);
  if (caret !== null) applyText(mention.body.value, caret);
}

function hover(index: number) {
  if (commandOpen.value) commandIndex.value = index;
  else personIndex.value = index;
}

/** Keys while the command list is open; true when the key was used. */
function onCommandKey(e: KeyboardEvent): boolean {
  const matches = commandMatches.value;
  const count = matches.length;
  const index = activeIndex.value;
  switch (e.key) {
    case 'ArrowDown':
      commandIndex.value = (index + 1) % count;
      break;
    case 'ArrowUp':
      commandIndex.value = (index - 1 + count) % count;
      break;
    case 'Home':
      commandIndex.value = 0;
      break;
    case 'End':
      commandIndex.value = count - 1;
      break;
    case 'Tab':
      if (e.shiftKey) return false;
      complete(matches[index]);
      break;
    case 'Enter':
      if (e.shiftKey) return false;
      if (commandQuery.value !== null && isExactChatCommand(matches[index], commandQuery.value)) {
        // Fully typed: send at once instead of completing.
        commandQuery.value = null;
        submit();
      } else {
        complete(matches[index]);
      }
      break;
    case 'Escape':
      commandQuery.value = null;
      break;
    default:
      return false;
  }
  e.preventDefault();
  e.stopPropagation();
  return true;
}

function submit() {
  const raw = content.value;
  const v = raw.trim();
  if ((!v && !props.canSend) || props.disabled || props.sending) return;
  commandQuery.value = null;
  mention.closePicker();
  // Mention ranges index into the untrimmed text: re-base them onto `v`.
  const lead = raw.length - raw.trimStart().length;
  const mentions = mentionsOn.value
    ? mention.mentions.value
        .map((m) => ({ ...m, start: m.start - lead, end: m.end - lead }))
        .filter((m) => m.start >= 0 && m.end <= v.length)
    : [];
  const invocation =
    v && props.commands.length ? parseChatCommand(v, props.commands, mentions) : null;
  if (invocation) emit('command', invocation);
  else if (mentionsOn.value) emit('send', v, mentions);
  else emit('send', v);
}

function onKey(e: KeyboardEvent) {
  if (e.isComposing) return;
  if (commandOpen.value && onCommandKey(e)) return;
  if (mentionOpen.value) {
    const handled = mention.handleKeydown(e);
    if (typeof handled === 'number') {
      applyText(mention.body.value, handled);
      return;
    }
    if (handled) return;
  }
  if (props.formatShortcuts && (e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey) {
    const mark = e.key?.length === 1 ? FORMAT_MARKS[e.key.toLowerCase()] : undefined;
    if (mark) {
      e.preventDefault();
      wrapSelection(mark, mark);
      return;
    }
  }
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    submit();
  }
}

function clear() {
  content.value = '';
  mention.reset('');
  commandQuery.value = null;
  nextTick(resize);
}

function onPaste(event: ClipboardEvent) {
  const files = Array.from(event.clipboardData?.files ?? []);
  if (files.length === 0) return;
  event.preventDefault();
  emit('files', files);
}

defineExpose({
  clear,
  focus: () => textarea.value?.focus(),
  resize,
  insertText,
  wrapSelection,
});
</script>

<template>
  <div
    class="nx-chat-composer"
    :class="{ disabled, sending, 'is-pill': shape === 'pill' }"
    data-testid="nx-chat-composer"
  >
    <div v-if="$slots.toolbar" class="nx-chat-composer__toolbar">
      <slot name="toolbar" />
    </div>
    <div class="nx-chat-composer__row">
      <div v-if="$slots.leading" class="nx-chat-composer__leading">
        <slot name="leading" />
      </div>
      <div class="nx-chat-composer__field">
        <ul
          v-if="pickerOpen"
          :id="listId"
          class="nx-chat-composer__picker"
          role="listbox"
          :aria-label="commandOpen ? commandsLabel : mentionsLabel"
        >
          <template v-if="commandOpen">
            <li
              v-for="(command, index) in commandMatches"
              :id="`${listId}-${index}`"
              :key="command.name"
              class="nx-chat-composer__option"
              :class="{ 'is-active': index === activeIndex }"
              role="option"
              :aria-selected="index === activeIndex"
              @mousedown.prevent
              @mouseenter="hover(index)"
              @click="pick(index)"
            >
              <span class="nx-chat-composer__command">{{ `/${command.name}` }}</span>
              <span v-if="command.hint" class="nx-chat-composer__hint">{{ command.hint }}</span>
              <span v-if="command.description" class="nx-chat-composer__description">
                {{ command.description }}
              </span>
            </li>
          </template>
          <template v-else>
            <li
              v-for="(person, index) in people"
              :id="`${listId}-${index}`"
              :key="person.id"
              class="nx-chat-composer__option"
              :class="{ 'is-active': index === activeIndex }"
              role="option"
              :aria-selected="index === activeIndex"
              @mousedown.prevent
              @mouseenter="hover(index)"
              @click="pick(index)"
            >
              <NxAvatar :name="person.name" :initials="person.initials" :size="20" decorative />
              <span class="nx-chat-composer__person">{{ person.name }}</span>
              <span v-if="person.shortName" class="nx-chat-composer__hint">
                {{ person.shortName }}
              </span>
            </li>
          </template>
        </ul>
        <textarea
          ref="textarea"
          v-model="content"
          class="nx-chat-composer__input"
          rows="1"
          :placeholder="placeholder"
          :disabled="disabled"
          :aria-label="inputLabel"
          :aria-autocomplete="pickersAvailable ? 'list' : undefined"
          :aria-controls="pickerOpen ? listId : undefined"
          :aria-activedescendant="pickerOpen ? `${listId}-${activeIndex}` : undefined"
          @keydown="onKey"
          @keyup="onKeyup"
          @input="onInput"
          @paste="onPaste"
          @click="detect"
          @blur="onBlur"
        />
        <div v-if="$slots['field-end']" class="nx-chat-composer__field-end">
          <slot name="field-end" />
        </div>
      </div>
      <button
        v-if="sending && cancellable"
        type="button"
        class="nx-chat-composer__btn cancel"
        :aria-label="cancelLabel"
        @click="emit('cancel')"
      >
        <slot name="cancel-icon">
          <Square class="nxt-icon-sm" />
        </slot>
      </button>
      <button
        v-else
        type="button"
        class="nx-chat-composer__btn send"
        :aria-label="sendLabel"
        :disabled="disabled || sending || (!canSend && !content.trim())"
        @click="submit"
      >
        <slot name="send-icon">
          <Send class="nxt-icon-sm" />
        </slot>
      </button>
    </div>
    <div v-if="$slots.footer" class="nx-chat-composer__footer">
      <slot name="footer" />
    </div>
  </div>
</template>

<style scoped>
.nx-chat-composer {
  display: flex;
  flex-direction: column;
  gap: calc(var(--nxt-space-xs) * 0.75);
  padding: calc(var(--nxt-space-xs) * 1.25) calc(var(--nxt-space-md) * 1.125)
    calc(var(--nxt-space-md) * 0.875);
  border-top: var(--nxt-hairline) solid var(--nxt-border);
  background: linear-gradient(
    180deg,
    var(--nxt-surface-bg) 0%,
    color-mix(in srgb, var(--nxt-brand-bg) 5%, transparent) 100%
  );
  flex: 0 0 auto;
}
/* Pill: the field carries the surface; send and the leading controls go round. */
.nx-chat-composer.is-pill {
  border-top: 0;
  background: transparent;
  padding: var(--nxt-space-2xs) var(--nxt-space-md) var(--nxt-space-sm);
}
.nx-chat-composer.is-pill .nx-chat-composer__input {
  border-color: var(--nxt-border-subtle);
  border-radius: calc(var(--nxt-size-unit) * 1.25);
  padding-inline: var(--nxt-space-md) calc(var(--nxt-size-unit) * 2.5);
  padding-block: calc(var(--nxt-size-unit) * 0.5);
  min-height: calc(var(--nxt-size-unit) * 2.5);
  background: var(--nxt-surface-raised-bg);
  box-shadow: var(--nxt-shadow-md);
}
.nx-chat-composer.is-pill .nx-chat-composer__btn {
  width: calc(var(--nxt-size-unit) * 2.5);
  height: calc(var(--nxt-size-unit) * 2.5);
  border-radius: var(--nxt-radius-full);
}
.nx-chat-composer__field-end {
  position: absolute;
  inset-inline-end: var(--nxt-space-2xs);
  bottom: calc(var(--nxt-space-2xs) * 0.9);
  display: flex;
  align-items: center;
}
.nx-chat-composer__toolbar {
  display: flex;
  align-items: center;
  gap: calc(var(--nxt-space-xs) * 0.75);
}
.nx-chat-composer__row {
  display: flex;
  align-items: flex-end;
  gap: var(--nxt-space-xs);
}
.nx-chat-composer__leading {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: var(--nxt-space-2xs);
  min-height: var(--nxt-control-size-lg);
}
.nx-chat-composer__field {
  position: relative;
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
}
.nx-chat-composer__input {
  flex: 1 1 auto;
  min-width: 0;
  resize: none;
  border: var(--nxt-hairline) solid var(--nxt-border);
  border-radius: var(--nxt-radius-lg);
  padding: calc(var(--nxt-space-xs) * 1.25) calc(var(--nxt-space-md) * 0.875);
  font-family: inherit;
  font-size: var(--nxt-font-size-sm);
  line-height: var(--nxt-line-height-normal);
  color: var(--nxt-fg);
  background: var(--nxt-surface-bg);
  outline: none;
  max-height: calc(var(--nxt-size-unit) * 10);
  box-sizing: border-box;
  transition:
    border-color var(--nxt-motion-fast) ease,
    box-shadow var(--nxt-motion-fast) ease;
}
.nx-chat-composer__input:focus {
  border-color: var(--nxt-brand-bg);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-focus-ring-soft);
}
.nx-chat-composer__input:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.nx-chat-composer__input::placeholder {
  color: var(--nxt-fg-muted);
}

/* Command / people list, opening upwards over the thread. */
.nx-chat-composer__picker {
  position: absolute;
  inset-inline: 0;
  bottom: calc(100% + var(--nxt-space-2xs));
  margin: 0;
  padding: var(--nxt-space-2xs) 0;
  list-style: none;
  max-height: var(--nxt-area-size-lg);
  overflow-y: auto;
  background: var(--nxt-popover-bg);
  color: var(--nxt-popover-fg);
  border: var(--nxt-hairline) solid var(--nxt-popover-border);
  border-radius: var(--nxt-radius-md);
  box-shadow: var(--nxt-popover-shadow);
  z-index: var(--nxt-z-dropdown);
}
.nx-chat-composer__option {
  display: flex;
  align-items: center;
  gap: var(--nxt-space-xs);
  padding: var(--nxt-space-xs) var(--nxt-space-sm);
  font-size: var(--nxt-font-size-sm);
  cursor: pointer;
}
.nx-chat-composer__option.is-active {
  background: var(--nxt-brand-subtle-bg);
  color: var(--nxt-brand-subtle-fg);
}
.nx-chat-composer__command {
  font-family: var(--nxt-font-mono);
  font-weight: var(--nxt-font-weight-semibold);
}
.nx-chat-composer__hint {
  font-family: var(--nxt-font-mono);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
.nx-chat-composer__description {
  margin-inline-start: auto;
  padding-inline-start: var(--nxt-space-sm);
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
  text-align: end;
}
.nx-chat-composer__person {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.nx-chat-composer__btn {
  flex: 0 0 auto;
  width: var(--nxt-control-size-lg);
  height: var(--nxt-control-size-lg);
  border: 0;
  border-radius: var(--nxt-radius-lg);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition:
    background var(--nxt-motion-fast) ease,
    opacity var(--nxt-motion-fast) ease;
}
.nx-chat-composer__btn:focus-visible {
  outline: calc(var(--nxt-hairline) * 2) solid var(--nxt-focus-ring);
  outline-offset: calc(var(--nxt-hairline) * 2);
}
.nx-chat-composer__btn.send {
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}
.nx-chat-composer__btn.send:hover:not(:disabled) {
  filter: brightness(1.06);
}
.nx-chat-composer__btn.send:disabled {
  background: var(--nxt-disabled-bg);
  color: var(--nxt-disabled-fg);
  cursor: not-allowed;
  filter: none;
}
.nx-chat-composer__btn.cancel {
  background: var(--nxt-danger-bg);
  color: var(--nxt-danger-fg);
}
.nx-chat-composer__btn.cancel:hover {
  background: var(--nxt-danger-bg-hover);
}
.nx-chat-composer__footer {
  font-size: var(--nxt-font-size-xs);
  color: var(--nxt-fg-muted);
}
</style>
